/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { PageTitle } from '../../../_metronic/layout/core';
import { CMStatsWidget, NotificationWidget } from '../../../_metronic/partials/widgets';
import axios from 'axios'; // Assuming axios is available for API calls
import { useSelector, shallowEqual } from 'react-redux';
import { RootState } from '../../../setup';
import { IAuthState } from '../../modules/auth/redux/AuthRedux';

interface LedgerEntry {
  month: string;
  nbMeetings: number;
  nbRecordings: number;
  nbParticipants: number;
  avgMeetingDuration: number;
  avgParticipantCount: number;
  baseCost: number;
  meetingCost: number;
  recordingCost: number;
  storageCost: number;
  // ... other properties from the ledger data
}

interface Notification {
  title: string;
  message: string;
  type?: 'Info' | 'Warning' | 'Error' | 'Success';
  severity?: number;
  active?: boolean;
}

interface DashboardStats {
  ledger: LedgerEntry[];
}

const DashboardPage: FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [meetingsData, setMeetingsData] = useState<number[]>([]);
  const [recordingsData, setRecordingsData] = useState<number[]>([]);
  const [participantsData, setParticipantsData] = useState<number[]>([]);
  const [avgMeetingDurationData, setAvgMeetingDurationData] = useState<number[]>([]);
  const [avgParticipantCountData, setAvgParticipantCountData] = useState<number[]>([]);
  const [costData, setCostData] = useState<number[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  interface User {
    user?: {
      consumerId?: string;
    };
  }

  const user: User = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as User;
  const consumerId = user?.user?.consumerId; // consumerId can be string or undefined
  const { accessToken } = useSelector<RootState, IAuthState>(({ auth }) => auth, shallowEqual);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      if (!consumerId) {
        console.warn('Consumer ID not available for dashboard stats.');
        return;
      }
      if (!accessToken) {
        console.warn('Access token not available.');
        return;
      }

      const options = {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
      };

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL_BACKEND_LOCAL}/dashboard-stats/${consumerId}`, options);
        const ledger = response.data.ledger;

        // Sort ledger by month to ensure correct order
        ledger.sort((a: LedgerEntry, b: LedgerEntry) => parseInt(a.month) - parseInt(b.month)); // Explicitly type a and b

        const monthCategories: string[] = [];

        // Generate the last 12 months
        const last12Months = Array.from({ length: 12 }).map((_, i) => {
          const date = new Date();
          date.setMonth(date.getMonth() - (11 - i)); // Go back 11 months from now for the first month, up to current month
          return {
            yearMonth: `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`,
            shortMonth: date.toLocaleString('default', { month: 'short' }),
          };
        });

        // Create maps for quick lookup of existing ledger entries
        const meetingsMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.nbMeetings)]));
        const recordingsMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.nbRecordings)]));
        const participantsMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.nbParticipants)]));
        const avgMeetingDurationMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.avgMeetingDuration)]));
        const avgParticipantCountMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.avgParticipantCount)]));
        const baseCostMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.baseCost)]));
        const meetingCostMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.meetingCost)]));
        const recordingCostMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.recordingCost)]));
        const storageCostMap = new Map(ledger.map((entry: LedgerEntry) => [`${entry.month}`, Number(entry.storageCost)]));

        const meetings: number[] = [];
        const recordings: number[] = [];
        const participants: number[] = [];
        const avgMeetingDuration: number[] = [];
        const avgParticipantCount: number[] = [];
        const costs: number[] = [];

        last12Months.forEach((month) => {
          meetings.push(Number(meetingsMap.get(month.yearMonth) || 0));
          recordings.push(Number(recordingsMap.get(month.yearMonth) || 0));
          participants.push(Number(participantsMap.get(month.yearMonth) || 0));
          avgMeetingDuration.push(Number(avgMeetingDurationMap.get(month.yearMonth) || 0));
          avgParticipantCount.push(Number(avgParticipantCountMap.get(month.yearMonth) || 0));
          
          const totalCost = Number(baseCostMap.get(month.yearMonth) || 0) +
                            Number(meetingCostMap.get(month.yearMonth) || 0) +
                            Number(recordingCostMap.get(month.yearMonth) || 0) +
                            Number(storageCostMap.get(month.yearMonth) || 0);
          costs.push(Number(totalCost.toFixed(2))); // Ensure it's a number
          
          monthCategories.push(month.shortMonth);
        });
        
        setMeetingsData(meetings);
        setRecordingsData(recordings);
        setParticipantsData(participants);
        setAvgMeetingDurationData(avgMeetingDuration);
        setAvgParticipantCountData(avgParticipantCount);
        setCostData(costs);
        setCategories(monthCategories);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        setMeetingsData([]);
        setRecordingsData([]);
        setParticipantsData([]);
        setAvgMeetingDurationData([]);
        setAvgParticipantCountData([]);
        setCostData([]);
        setCategories([]);
      }
    };

    fetchDashboardStats();
  }, [consumerId]);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!consumerId) {
        console.warn('Consumer ID not available for notifications.');
        return;
      }
      if (!accessToken) {
        console.warn('Access token not available.');
        return;
      }

      const options = {
        headers: {
          Authorization: `Bearer ${accessToken}`
        },
      };

      try {
        const response = await axios.get<Notification[]>(`${process.env.REACT_APP_API_URL_BACKEND_LOCAL}/notifications`, options);
        setNotifications(response.data);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setNotifications([]);
      }
    };

    fetchNotifications();
  }, [consumerId]);

  return (
    <>
      {/* begin::Row */}
        <div className='row gy-5 g-xl-8'>
      <div className='col-xxl-6'>
        <NotificationWidget className='card-xxl-stretch' notifications={notifications} />
      </div>
        <div className='col-xxl-6'>
          <CMStatsWidget
            className='card-xl-stretch mb-xl-8'
            chartColor='danger'
            chartHeight='100px'
            strokeColor='#cb1e46'
            title='Meetings'
            description='Number of meetings with atleast 2 participants'
            series={[
              {
                name: 'Meetings',
                data: meetingsData,
              },
            ]}
            categories={categories}
          />
        </div>
      <div className='col-xxl-6'>
        <CMStatsWidget
          className='card-xl-stretch mb-xl-8'
          chartColor='info'
          chartHeight='200px'
          strokeColor='#0BB783'
          title='Cost History (USD)'
          description='Your Monthly API Usage'
          series={[
            {
              name: 'Cost (USD)',
              data: costData,
            },
          ]}
          categories={categories}
        />
      </div>
      <div className='col-xxl-6'>
        <CMStatsWidget
          className='card-xl-stretch mb-xl-8'
          chartColor='primary'
          chartHeight='100px'
          strokeColor='#3699FF'
          description='Number of recorded meetings'
          title='Recordings'
          series={[
              {
                name: 'Recordings',
                data: recordingsData,
              },
            ]}
           categories={categories}
          />
        </div>
      <div className='col-xxl-6'>
        <CMStatsWidget
          className='card-xl-stretch mb-xl-8'
          chartColor='success'
          chartHeight='100px'
          strokeColor='#0BB783'
          title='Participants Connected'
          description='Total non-unique participants connected on Clan Meeting'
          series={[
            {
              name: 'Participants',
              data: participantsData,
            },
          ]}
          categories={categories}
        />
      </div>
      <div className='col-xxl-6'>
        <CMStatsWidget
          className='card-xl-stretch mb-xl-8'
          chartColor='danger'
          chartHeight='100px'
          strokeColor='#cb1e46'
          title='Average Meeting Duration'
          description='Average duration of each meeting in minutes per month'
          series={[
            {
              name: 'Duration (min)',
              data: avgMeetingDurationData,
            },
          ]}
          categories={categories}
        />
      </div>
      <div className='col-xxl-6'>
        <CMStatsWidget
          className='card-xl-stretch mb-xl-8'
          chartColor='primary'
          chartHeight='100px'
          strokeColor='#3699FF'
          title='Average Participant Count'
          description='Average number of participants per call for each month'
          series={[
            {
              name: 'Participants',
              data: avgParticipantCountData,
            },
          ]}
          categories={categories}
        />
      </div>
    </div>
    {/* end::Row */}
  </>
  );
};

const DashboardWrapper: FC = () => {
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.DASHBOARD' })}</PageTitle>
      <div style={{ paddingLeft: '20px', paddingRight: '20px' }}>
      <DashboardPage />
      </div>
    </>
  )
}

export { DashboardWrapper }
